'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FormData {
  fullName: string;
  email: string;
  phone: string;
  company: string;
  projectType: string;
  budgetRange: string;
  timeline: string;
  message: string;
  gdprConsent: boolean;
}

interface ContactSectionProps {
  onWhatsAppClick: () => void;
}

const ContactSection = ({ onWhatsAppClick }: ContactSectionProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    email: '',
    phone: '',
    company: '',
    projectType: '',
    budgetRange: '',
    timeline: '',
    message: '',
    gdprConsent: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const projectTypes = [
    'EPC Services',
    'Workshop & Assembly',
    'Retail & Dealership',
    'Maintenance Contract',
    'Consultation',
    'Other'
  ];

  const budgetRanges = [
    'Under $50,000',
    '$50,000 - $100,000',
    '$100,000 - $500,000',
    '$500,000 - $1,000,000',
    'Over $1,000,000'
  ];

  const timelines = [
    'Urgent (Within 1 month)',
    'Short-term (1-3 months)',
    'Medium-term (3-6 months)',
    'Long-term (6+ months)',
    'Planning Phase'
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isHydrated) return;

    setIsSubmitting(true);

    await new Promise(resolve => setTimeout(resolve, 2000));

    setIsSubmitting(false);
    setSubmitSuccess(true);

    setTimeout(() => {
      setSubmitSuccess(false);
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        company: '',
        projectType: '',
        budgetRange: '',
        timeline: '',
        message: '',
        gdprConsent: false
      });
    }, 3000);
  };

  return (
    <section id="contact" className="py-20 bg-card">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-headline text-4xl md:text-5xl font-bold text-primary mb-4">
              Start Your Project Today
            </h2>
            <p className="font-body text-xl text-muted-foreground max-w-3xl mx-auto">
              Get a customized quote for your electrical and mechanical engineering needs
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit} className="bg-surface rounded-lg p-8 shadow-card">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="fullName" className="block font-body font-semibold text-foreground mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 rounded-lg border border-border bg-input focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-250"
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block font-body font-semibold text-foreground mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 rounded-lg border border-border bg-input focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-250"
                      placeholder="john@company.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block font-body font-semibold text-foreground mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 rounded-lg border border-border bg-input focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-250"
                      placeholder="+250 XXX XXX XXX"
                    />
                  </div>

                  <div>
                    <label htmlFor="company" className="block font-body font-semibold text-foreground mb-2">
                      Company Name *
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 rounded-lg border border-border bg-input focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-250"
                      placeholder="Your Company Ltd"
                    />
                  </div>

                  <div>
                    <label htmlFor="projectType" className="block font-body font-semibold text-foreground mb-2">
                      Project Type *
                    </label>
                    <select
                      id="projectType"
                      name="projectType"
                      value={formData.projectType}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 rounded-lg border border-border bg-input focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-250"
                    >
                      <option value="">Select project type</option>
                      {projectTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="budgetRange" className="block font-body font-semibold text-foreground mb-2">
                      Budget Range *
                    </label>
                    <select
                      id="budgetRange"
                      name="budgetRange"
                      value={formData.budgetRange}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 rounded-lg border border-border bg-input focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-250"
                    >
                      <option value="">Select budget range</option>
                      {budgetRanges.map(range => (
                        <option key={range} value={range}>{range}</option>
                      ))}
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label htmlFor="timeline" className="block font-body font-semibold text-foreground mb-2">
                      Project Timeline *
                    </label>
                    <select
                      id="timeline"
                      name="timeline"
                      value={formData.timeline}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 rounded-lg border border-border bg-input focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-250"
                    >
                      <option value="">Select timeline</option>
                      {timelines.map(time => (
                        <option key={time} value={time}>{time}</option>
                      ))}
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label htmlFor="message" className="block font-body font-semibold text-foreground mb-2">
                      Project Details
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={4}
                      className="w-full px-4 py-3 rounded-lg border border-border bg-input focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-250 resize-none"
                      placeholder="Please provide details about your project requirements..."
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="gdprConsent"
                      checked={formData.gdprConsent}
                      onChange={handleInputChange}
                      required
                      className="mt-1 w-5 h-5 rounded border-border text-primary focus:ring-2 focus:ring-primary"
                    />
                    <span className="font-body text-sm text-muted-foreground">
                      I consent to New Capital for General Contracting Ltd processing my personal data to provide project quotations and related communications. Your data will be handled in accordance with GDPR regulations and our privacy policy. *
                    </span>
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting || !formData.gdprConsent}
                  className="w-full bg-accent hover:bg-accent/90 disabled:bg-muted disabled:cursor-not-allowed text-white font-cta font-bold px-8 py-4 rounded-lg transition-all duration-250 transform hover:scale-105 flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <Icon name="ArrowPathIcon" size={24} variant="outline" className="animate-spin" />
                      Submitting...
                    </>
                  ) : submitSuccess ? (
                    <>
                      <Icon name="CheckCircleIcon" size={24} variant="solid" />
                      Request Submitted!
                    </>
                  ) : (
                    <>
                      <Icon name="PaperAirplaneIcon" size={24} variant="solid" />
                      Request Project Quote
                    </>
                  )}
                </button>
              </form>
            </div>

            <div className="space-y-6">
              <div className="bg-surface rounded-lg p-6 shadow-card">
                <h3 className="font-headline text-xl font-bold text-primary mb-4">Contact Information</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Icon name="MapPinIcon" size={24} variant="solid" className="text-accent flex-shrink-0" />
                    <div>
                      <div className="font-body font-semibold text-foreground">Head Office</div>
                      <div className="font-body text-sm text-muted-foreground">KG 11 Ave, Kigali, Rwanda</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Icon name="PhoneIcon" size={24} variant="solid" className="text-accent flex-shrink-0" />
                    <div>
                      <div className="font-body font-semibold text-foreground">Phone</div>
                      <div className="font-body text-sm text-muted-foreground">+250 788 123 456</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Icon name="EnvelopeIcon" size={24} variant="solid" className="text-accent flex-shrink-0" />
                    <div>
                      <div className="font-body font-semibold text-foreground">Email</div>
                      <div className="font-body text-sm text-muted-foreground">eng@new-capital.capital</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-success to-primary rounded-lg p-6 text-white shadow-card">
                <h3 className="font-headline text-xl font-bold mb-3">Instant Response</h3>
                <p className="font-body text-sm mb-4 opacity-90">
                  Need immediate assistance? Chat with our team on WhatsApp for quick responses to your inquiries.
                </p>
                <button
                  onClick={onWhatsAppClick}
                  className="w-full bg-white hover:bg-white/90 text-primary font-cta font-bold px-6 py-3 rounded-lg transition-all duration-250 flex items-center justify-center gap-2"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2C6.477 2 2 6.477 2 12C2 13.89 2.525 15.66 3.438 17.168L2.546 20.2L5.653 19.327C7.117 20.145 8.801 20.625 10.594 20.625C16.117 20.625 20.594 16.148 20.594 10.625C20.594 5.102 16.117 2 12 2Z" fill="currentColor"/>
                  </svg>
                  Chat on WhatsApp
                </button>
              </div>

              <div className="bg-surface rounded-lg p-6 shadow-card">
                <h3 className="font-headline text-xl font-bold text-primary mb-4">Office Hours</h3>
                <div className="space-y-2 font-body text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Monday - Friday</span>
                    <span className="font-semibold text-foreground">8:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Saturday</span>
                    <span className="font-semibold text-foreground">9:00 AM - 1:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Sunday</span>
                    <span className="font-semibold text-foreground">Closed</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-16 bg-surface rounded-lg overflow-hidden shadow-card">
            <div className="h-96">
              <iframe
                width="100%"
                height="100%"
                loading="lazy"
                title="New Capital EPC Head Office Location"
                referrerPolicy="no-referrer-when-downgrade"
                src="https://www.google.com/maps?q=-1.9536,30.0606&z=14&output=embed"
                className="border-0"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;